import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, Plus, Send, CheckCircle, Clock, FileCheck, Eye, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { clientsAPI } from '../lib/clientsAPI';
import { formatAUDate } from '../lib/dateUtils';
import { DynamicFormRenderer } from '../components/forms/DynamicFormRenderer';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  expired: 'bg-muted text-muted-foreground border-border'
};

export default function Forms() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    initializeTemplates();
    loadData();
  }, []);

  const initializeTemplates = async () => {
    try {
      await api.post('/api/admin/init-form-templates');
    } catch (error) {
      console.log('Templates already initialized');
    }
  };

  const loadData = async () => {
    try {
      const [templatesRes, assignmentsRes, clientsRes] = await Promise.all([
        api.get('/api/forms/templates'),
        api.get('/api/forms/assignments'),
        clientsAPI.list({ status: 'active' })
      ]);
      
      setTemplates(templatesRes.data);
      setAssignments(assignmentsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssign = (template) => {
    setSelectedTemplate(template);
    setAssignDialogOpen(true);
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setPreviewDialogOpen(true);
  };

  // For preview form data (not saved, just for display)
  const [previewFormData, setPreviewFormData] = useState({});
  
  const handlePreviewFieldChange = (fieldId, value) => {
    setPreviewFormData({ ...previewFormData, [fieldId]: value });
  };

  const handleOpenEdit = (template) => {
    setEditTemplate(template);
    setEditForm({
      name: template.name,
      description: template.description,
      template_type: template.template_type,
      fields_schema: JSON.parse(JSON.stringify(template.fields_schema)) // Deep copy
    });
    setPreviewDialogOpen(false);
    setEditDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/forms/templates/${editTemplate.id}`, editForm);
      toast.success('Template updated successfully');
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (template) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/api/forms/templates/${template.id}`);
      toast.success('Template deleted successfully');
      setPreviewDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleAssign = async () => {
    if (!selectedClientId) {
      toast.error('Please select a client');
      return;
    }

    setAssigning(true);
    try {
      await api.post('/api/forms/assignments', {
        template_id: selectedTemplate.id,
        client_id: selectedClientId,
        due_date: null
      });
      
      toast.success('Form assigned successfully');
      setAssignDialogOpen(false);
      setSelectedClientId('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign form');
    } finally {
      setAssigning(false);
    }
  };

  const handleViewSubmission = async (assignment) => {
    try {
      const response = await api.get('/api/forms/submissions', {
        params: { assignment_id: assignment.id }
      });
      
      if (response.data.length > 0) {
        const submission = response.data[0];
        
        // Get template for field labels
        const template = templates.find(t => t.id === assignment.template_id);
        
        setSelectedSubmission({
          ...submission,
          assignment,
          template
        });
        setViewDialogOpen(true);
      } else {
        toast.error('No submission found');
      }
    } catch (error) {
      toast.error('Failed to load submission');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
          Forms
        </h1>
        <p className="text-muted-foreground mt-1">Manage form templates and client assignments</p>
      </div>

      <Tabs defaultValue="templates" className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="templates">
              <FileText className="w-4 h-4 mr-2" />
              Form Templates ({templates.length})
            </TabsTrigger>
            <TabsTrigger value="assignments">
              <Send className="w-4 h-4 mr-2" />
              Assigned Forms ({assignments.length})
            </TabsTrigger>
          </TabsList>
          
          <Button onClick={() => navigate('/forms/builder')} className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" />
            Create Custom Template
          </Button>
        </div>

        <TabsContent value="templates" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>
                          {template.name}
                        </CardTitle>
                        <Badge variant="outline" className="mt-1">
                          {template.fields_schema.length} fields
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <CardDescription className="mt-3">{template.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button 
                      onClick={() => handlePreviewTemplate(template)} 
                      variant="outline"
                      className="w-full"
                      size="sm"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      View Template Details
                    </Button>
                    <Button 
                      onClick={() => handleOpenAssign(template)} 
                      className="w-full bg-primary hover:bg-primary/90"
                      size="sm"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Assign to Client
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              {assignments.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileCheck className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p>No forms assigned yet</p>
                  <p className="text-sm mt-1">Assign forms to clients using the Templates tab</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold text-foreground/90">Form Template</TableHead>
                      <TableHead className="font-semibold text-foreground/90">Client</TableHead>
                      <TableHead className="font-semibold text-foreground/90">Status</TableHead>
                      <TableHead className="font-semibold text-foreground/90">Assigned</TableHead>
                      <TableHead className="font-semibold text-foreground/90">Completed</TableHead>
                      <TableHead className="font-semibold text-foreground/90 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell className="font-medium text-foreground">{assignment.template_name}</TableCell>
                        <TableCell className="text-muted-foreground">{assignment.client_name}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[assignment.status]}>
                            {assignment.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                            {assignment.status === 'completed' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">{formatAUDate(assignment.assigned_at)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {assignment.completed_at ? formatAUDate(assignment.completed_at) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {assignment.status === 'completed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewSubmission(assignment)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign Form Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign {selectedTemplate?.name} to Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <FileText className="w-4 h-4 inline mr-1" />
                {selectedTemplate?.description}
              </p>
              <p className="text-xs text-blue-600 mt-2">
                {selectedTemplate?.fields_schema.length} fields to complete
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_select">Select Client</Label>
              <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                <SelectTrigger id="client_select">
                  <SelectValue placeholder="Choose a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssign} disabled={assigning} className="bg-primary hover:bg-primary/90">
                {assigning ? 'Assigning...' : 'Assign Form'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Submission Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Form Submission - {selectedSubmission?.template?.name}</DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                <div>
                  <Label className="text-muted-foreground text-sm">Client</Label>
                  <p className="font-medium text-foreground">{selectedSubmission.assignment.client_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-sm">Submitted</Label>
                  <p className="font-medium text-foreground">{formatAUDate(selectedSubmission.submitted_at)}</p>
                </div>
              </div>

              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {selectedSubmission.template?.fields_schema.map((field) => {
                  const value = selectedSubmission.form_data[field.id];
                  
                  return (
                    <div key={field.id} className="p-3 bg-muted/50 rounded-lg">
                      <Label className="text-muted-foreground text-sm">{field.label}</Label>
                      {field.type === 'checkbox' ? (
                        <p className="font-medium text-foreground mt-1">
                          {value === true || value === 'true' ? '✓ Yes' : '✗ No'}
                        </p>
                      ) : (
                        <p className="font-medium text-foreground mt-1 whitespace-pre-wrap">
                          {value || <span className="text-muted-foreground/70">Not provided</span>}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-muted-foreground">{previewTemplate.description}</p>
                <Badge variant="outline">{previewTemplate.fields_schema.length} fields</Badge>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <FileText className="w-4 h-4 inline mr-1" />
                  This is a preview of how the form will appear to clients
                </p>
              </div>

              {/* Render Actual Form */}
              <div className="p-6 bg-card border-2 border-border rounded-lg">
                <DynamicFormRenderer
                  fields={previewTemplate.fields_schema}
                  formData={previewFormData}
                  onChange={handlePreviewFieldChange}
                  errors={{}}
                />
              </div>

              <div className="flex justify-between pt-4 border-t">
                <Button 
                  variant="outline"
                  onClick={() => handleDeleteTemplate(previewTemplate)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Template
                </Button>
                
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
                    Close
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => handleOpenEdit(previewTemplate)}
                  >
                    Edit Template
                  </Button>
                  <Button 
                    onClick={() => {
                      setPreviewDialogOpen(false);
                      handleOpenAssign(previewTemplate);
                    }}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Assign to Client
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Template: {editTemplate?.name}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_name">Template Name</Label>
                <Input
                  id="edit_name"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_desc">Description</Label>
                <Textarea
                  id="edit_desc"
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={2}
                />
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium text-foreground/90 mb-2">
                  Fields ({editForm.fields_schema?.length || 0})
                </p>
                <p className="text-xs text-muted-foreground">
                  Field editing will be available in the Form Builder feature (coming soon).
                  You can currently edit the template name and description.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
